// src/Components/Customers/CustomerLedgerReport.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../Helpers/config.json";
import { formatAmount } from "../../Utils/formatAmount";
import { titleCase } from "../../Utils/titleCaseFunction";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { BackButton } from "../../Utils/FormInputs/BackButton";
import Label from "../../Utils/FormInputs/Label";
import { Container } from "../../Utils/styledComponents";
import { MuiPaper } from "../../Utils/Tables/CustomTable";
import {
  LinearProgress,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  Grid,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";

const CustomerLedgerReport = ({ actions, snackbars, match }) => {
  const { customer_id } = match.params;

  const [ledger, setLedger] = useState({
    customer: {},
    period: { from: "", to: "" },
    opening_balance: 0,
    closing_balance: 0,
    transactions: [],
    summary: {},
  });
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Auto-set last 90 days
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setToDate(today);
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    setFromDate(ninetyDaysAgo.toISOString().split("T")[0]);
  }, []);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.epharmUrl}/Creport/customer-ledger/${customer_id}`,
        {
          params: {
            start_date: fromDate || undefined,
            end_date: toDate || undefined,
          },
        }
      );

      if (res.data.success) {
        setLedger(res.data.data);
      } else {
        actions.snackbarActions.errorResponse("Failed to load ledger");
      }
    } catch (err) {
      actions.snackbarActions.errorResponse("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      const delay = setTimeout(fetchLedger, 500);
      return () => clearTimeout(delay);
    }
  }, [fromDate, toDate, customer_id]);

  const { customer, period, opening_balance, closing_balance, transactions, summary } = ledger;
  const { open, message, variant } = snackbars;

  // Search filter
  const filteredTxns = transactions.filter(
    (t) =>
      t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.ref_no?.includes(searchTerm) ||
      t.mode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Customer Ledger Report"
        heading="Customers"
        subHeading={`${titleCase(customer.name || "Loading...")} Ledger`}
        link={`/customer-ledger/${customer_id}`}
      />

      <PharmacySnackbar
        open={open}
        message={message}
        variant={variant}
        handleCloseBar={() => actions.snackbarActions.hideSnackBar()}
      />

      <Container>
        <div className="mb-4">
          <BackButton to="/managecustomer/manage_customer" text="Back to Customers" />
        </div>

        {/* Customer Info Card */}
        <Card className="shadow-sm mb-4 border-start border-primary">
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <h3 className="mb-1">{titleCase(customer.name || "Walk-in Customer")}</h3>
                <p className="text-muted mb-1">
                  Mobile: <strong>{customer.mobile || "N/A"}</strong> | 
                  Address: {customer.address || "N/A"}
                </p>
                <small>
                  Credit Limit: <strong className="text-info">{formatAmount(customer.credit_limit || 0)}</strong>
                </small>
              </Grid>
              <Grid item xs={12} md={4} className="text-md-end">
                <div className="mb-2">
                  <strong>Period:</strong> {period.from} to {period.to}
                </div>
                <h4 className={`mb-0 ${closing_balance > 0 ? "text-danger" : "text-success"}`}>
                  Outstanding: <strong>{formatAmount(closing_balance)}</strong>
                </h4>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filters
        <Card className="mb-4 shadow-sm">
          <CardContent>
            <Grid container spacing={3} alignItems="end">
              <Grid item xs={12} sm={4} md={3}>
                <Label name="From Date" />
                <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Label name="To Date" />
                <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Label name="Search" />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Invoice, mode, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button fullWidth variant="contained" color="primary" onClick={fetchLedger}>
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} className="mb-4">
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-info shadow-sm">
              <CardContent>
                <div className="text-uppercase text-info small mb-1">Opening Balance</div>
                <h4 className="mb-0">{formatAmount(opening_balance)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-danger shadow-sm">
              <CardContent>
                <div className="text-uppercase text-danger small mb-1">Total Credit Sales</div>
                <h4 className="mb-0 text-danger">{formatAmount(summary.total_credit || 0)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-success shadow-sm">
              <CardContent>
                <div className="text-uppercase text-success small mb-1">Total Received</div>
                <h4 className="mb-0 text-success">{formatAmount(summary.total_payments_received || 0)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-warning shadow-sm">
              <CardContent>
                <div className="text-uppercase text-warning small mb-1">Cash Sales</div>
                <h4 className="mb-0 text-warning">{formatAmount(summary.total_cash_sales || 0)}</h4>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* PROFESSIONAL LEDGER TABLE */}
        <MuiPaper>
          {loading && <LinearProgress />}

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Ref No</th>
                  <th>Description</th>
                  <th>Mode</th>
                  <th className="text-end">Debit (Dr)</th>
                  <th className="text-end">Credit (Cr)</th>
                  <th className="text-end">Balance</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening Balance Row */}
                {opening_balance > 0 && (
                  <tr className="table-secondary fw-bold">
                    <td colSpan="6" className="text-end">Opening Balance</td>
                    <td className="text-end">{formatAmount(opening_balance)}</td>
                  </tr>
                )}

                {filteredTxns.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  filteredTxns.map((txn, idx) => (
                    <tr key={idx}>
                      <td>{txn.date}</td>
                      <td>
                        <span className="badge bg-primary">{txn.ref_no}</span>
                      </td>
                      <td>{txn.description}</td>
                      <td>
                        <span
                          className={`badge ${
                            txn.mode === "Credit"
                              ? "bg-danger"
                              : txn.mode === "Cash"
                              ? "bg-success"
                              : "bg-info"
                          }`}
                        >
                          {txn.mode}
                        </span>
                      </td>
                      <td className="text-end text-danger fw-bold">
                        {txn.debit > 0 ? formatAmount(txn.debit) : ""}
                      </td>
                      <td className="text-end text-success fw-bold">
                        {txn.credit > 0 ? formatAmount(txn.credit) : ""}
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(txn.balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </MuiPaper>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbars,
});

const mapDispatchToProps = (dispatch) => ({
  actions: {
    snackbarActions: bindActionCreators(snackbarActions, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerLedgerReport);